import React from "react";
import { useTranslation } from "react-i18next";

export default function Helpgl() {
  const { t } = useTranslation("help");

  return (
    <div className="whx-gitlab-guide">

      {/* =====================================================
          HEADER
      ===================================================== */}
      <div className="guide-header">
        <h2>
          {t("Helpgl.header.title", "Guide d'aide sur l'intégration CI/CD GitLab WhiteHaX")}
        </h2>

        <p>
          {t(
            "Helpgl.header.description",
            "Utilisation de GitLab CI/CD, du transfert de l'URL cible après le déploiement, de l'intégration de l'API REST WhiteHaX et des artefacts de rapport GitLab DAST."
          )}
        </p>
      </div>


      {/* =====================================================
          RECOMMENDED USE
      ===================================================== */}
      <section className="guide-section">

        <h2>{t("Helpgl.recommendedUse.title", "Utilisation recommandée")}</h2>

        <ol>
          <li>
            {t(
              "Helpgl.recommendedUse.item1",
              "Considérez le flux de travail et la passerelle SARIF comme une structure réutilisable. Ne remplissez l'adaptateur d'API REST WhiteHaX qu'une fois que les détails exacts du point de terminaison de la plateforme sont disponibles."
            )}
          </li>

          <li>
            {t(
              "Helpgl.recommendedUse.item2",
              "Les noms de fichiers, chemins d'accès et noms de variables figurant dans ce guide ne sont que des exemples. Vous pouvez enregistrer les fichiers (script Python et .yml) et modifier les variables avec des noms significatifs pour vous et votre projet."
            )}
          </li>
        </ol>

      </section>


      {/* =====================================================
          ASSUMPTIONS
      ===================================================== */}
      <section className="guide-section">

        <h2>{t("Helpgl.assumptions.title", "Hypothèses")}</h2>

        <ol>

          <li>
            {t(
              "Helpgl.assumptions.item1",
              "Que vous possédez déjà un compte utilisateur sur la plateforme WhiteHaX AI-ASM et que vous avez récupéré la clé d'API REST depuis la plateforme."
            )}
          </li>

          <li>
            {t(
              "Helpgl.assumptions.item2",
              "Que vous utiliserez un profil de test et un point de terminaison d'IA créés précédemment, ou que vous en créerez de nouveaux à l'aide de l'API REST WhiteHaX."
            )}
          </li>

        </ol>

      </section>


      {/* =====================================================
          WHAT THIS INTEGRATION DOES
      ===================================================== */}
      <section className="guide-section">

        <h2>
          {t("Helpgl.whatThisIntegrationDoes.title", "1. Ce que fait cette intégration")}
        </h2>

        <p>
          {t(
            "Helpgl.whatThisIntegrationDoes.p1",
            "Ce guide décrit comment exécuter une étape de vérification DAST WhiteHaX après un déploiement GitLab et publier le résultat en tant qu'artefact de rapport de sécurité DAST natif de GitLab."
          )}
        </p>

        <p>
          {t(
            "Helpgl.whatThisIntegrationDoes.p2_part1",
            "GitLab intègre les fichiers déclarés sous"
          )}
          <code> artifacts:reports:dast </code>
          {t(
            "Helpgl.whatThisIntegrationDoes.p2_part2",
            "et peut afficher les résultats dans les widgets de sécurité des demandes de fusion, l'onglet Sécurité du pipeline, le rapport de vulnérabilité du projet et les tableaux de bord de sécurité lorsque le niveau de sécurité GitLab requis est disponible."
          )}
        </p>


        <div className="info-box">

          <strong>
            {t(
              "Helpgl.whatThisIntegrationDoes.infoBox.title",
              "Remarque : Remarque sur la cible de déploiement"
            )}
          </strong>

          <p>
            {t(
              "Helpgl.whatThisIntegrationDoes.infoBox.p1",
              "La vérification du statut de déploiement AWS ou les contrôles post-déploiement AWS s'appliquent uniquement lorsque le serveur d'exécution de l'application est déployé dans AWS."
            )}
          </p>

          <p>
            {t(
              "Helpgl.whatThisIntegrationDoes.infoBox.p2_part1",
              "Si la cible d'exécution est locale, sur le poste de travail d'un développeur ou au sein d'un réseau privé, transmettez explicitement la cible via"
            )}
            <code> WHITEHAX_TARGET_URL</code>,
            <code> TARGET_URL</code>
            {t(
              "Helpgl.whatThisIntegrationDoes.infoBox.p2_part2",
              ", ou"
            )}
            <code> CI_ENVIRONMENT_URL</code>
            {t(
              "Helpgl.whatThisIntegrationDoes.infoBox.p3_part3",
              " et configurez le script Python pour gérer la résolution de la cible locale/privée."
            )}
          </p>

          <p>
            {t(
              "Helpgl.whatThisIntegrationDoes.infoBox.p3",
              "Pour les exécuteurs GitLab partagés, localhost désigne le conteneur de l'exécuteur et non la machine du développeur. Utilisez un exécuteur auto-hébergé, un point de terminaison accessible via VPN, une URL de staging ou un tunnel contrôlé lorsqu'une analyse d'exécution locale/privée est requise."
            )}
          </p>

        </div>


        <h3>
          {t("Helpgl.whatThisIntegrationDoes.requirements.title", "Exigences")}
        </h3>

        <ul className="bullet-list">

          <li>
            {t(
              "Helpgl.whatThisIntegrationDoes.requirements.item1",
              "L'étape de déploiement doit transmettre l'URL d'exécution en direct à l'étape de vérification de sécurité."
            )}
          </li>

          <li>
            {t(
              "Helpgl.whatThisIntegrationDoes.requirements.item2_prefix",
              "Le travail d'analyse doit écrire"
            )}
            <code> gl-dast-report.json </code>
            {t(
              "Helpgl.whatThisIntegrationDoes.requirements.item2_suffix",
              "à la racine de l'espace de travail du dépôt."
            )}
          </li>

          <li>
            {t(
              "Helpgl.whatThisIntegrationDoes.requirements.item3_prefix",
              "L'artefact doit être déclaré sous"
            )}
            <code> artifacts:reports:dast </code>
            {t(
              "Helpgl.whatThisIntegrationDoes.requirements.item3_suffix",
              "."
            )}
          </li>

          <li>
            {t(
              "Helpgl.whatThisIntegrationDoes.requirements.item4",
              "Le rapport doit faire l'objet d'une validation locale du schéma avant d'être considéré comme prêt pour la production."
            )}
          </li>

        </ul>

      </section>


      <section className="guide-section">

        <h2>
          {t("Helpgl.importantGitlabDifference.title", "2. Différence importante avec GitLab")}
        </h2>

        <p>
          {t(
            "Helpgl.importantGitlabDifference.p1",
            "GitLab CI ne fournit pas d'équivalent direct à l'écouteur d'événements deployment_status de GitHub dans cet exemple."
          )}
        </p>

        <p>
          {t(
            "Helpgl.importantGitlabDifference.p2_part1",
            "Le modèle le plus sûr consiste à exécuter WhiteHaX lors d'une étape ultérieure après le déploiement, ou à déclencher un pipeline distinct et à transmettre"
          )}
          <code> WHITEHAX_TARGET_URL </code>
          {t(
            "Helpgl.importantGitlabDifference.p2_part2",
            "explicitement."
          )}
        </p>

        <p>
          <code>CI_ENVIRONMENT_URL</code>
          {t(
            "Helpgl.importantGitlabDifference.p3",
            " est utile lorsque le travail possède un contexte d'environnement, mais un travail indépendant ultérieur ne doit pas supposer qu'il est automatiquement disponible à moins que le pipeline ne le transmette."
          )}
        </p>

      </section>


      {/* =====================================================
          REPOSITORY LAYOUT
      ===================================================== */}
      <section className="guide-section">

        <h2>
          {t("Helpgl.repositoryLayout.title", "3. Disposition du dépôt")}
        </h2>

        <pre className="code-block">
{`.gitlab-ci.yml
whitehax_scans/
  whitehax_tests.py
  requirements.txt`}
        </pre>

        <p>
          {t(
            "Helpgl.repositoryLayout.p1",
            "requirements.txt doit inclure requests et toutes les dépendances du client/SDK WhiteHax utilisées par l'adaptateur REST réel."
          )}
        </p>

      </section>


      {/* =====================================================
          GITLAB CI YAML
      ===================================================== */}
      <section className="guide-section">

        <h2>
          {t("Helpgl.recommendedGitlabCiYaml.title", "4. Fichier .gitlab-ci.yml recommandé")}
        </h2>

        <p>
          {t(
            "Helpgl.recommendedGitlabCiYaml.p1_prefix",
            "Enregistrez l'exemple suivant sous"
          )}
          <code> .gitlab-ci.yml </code>
          {t(
            "Helpgl.recommendedGitlabCiYaml.p1_suffix",
            "ou fusionnez le travail dans un pipeline existant."
          )}
        </p>

        <p>
          {t(
            "Helpgl.recommendedGitlabCiYaml.p2_prefix",
            "Remplacez"
          )}
          <code> deploy_to_aws </code>
          {t(
            "Helpgl.recommendedGitlabCiYaml.p2_suffix",
            "par le véritable travail de déploiement du dépôt."
          )}
        </p>

        <p>
          {t(
            "Helpgl.recommendedGitlabCiYaml.p3",
            "Le transfert dotenv est inclus pour rendre l'URL de la cible d'exécution explicite et fiable."
          )}
        </p>


        <pre className="code-block">
{`stages:
  - deploy
  - security-verification

# Exemple : votre véritable tâche de déploiement doit définir ou publier
# l'URL d'exécution déployée.
#
# Si la tâche de déploiement définit déjà environment:url,
# GitLab peut exposer CI_ENVIRONMENT_URL au sein de cette tâche.
#
# Pour une tâche ultérieure, transmettez explicitement l'URL via
# dotenv ou des variables.

deploy_to_aws:
  stage: deploy

  script:
    - echo "Deploy application to AWS here"
    - echo "WHITEHAX_TARGET_URL=https://staging.example.com" > deploy.env

  artifacts:
    reports:
      dotenv: deploy.env


whitehax_dast_scan:
  stage: security-verification

  image: python:3.11-slim

  needs:
    - job: deploy_to_aws
      artifacts: true

  rules:
    - if: '$WHITEHAX_TARGET_URL != ""'
    - if: '$CI_PIPELINE_SOURCE == "trigger" && $WHITEHAX_TARGET_URL != ""'
    - when: never

  variables:
    TARGET_URL: "$WHITEHAX_TARGET_URL"
    WHITEHAX_FAIL_ON_ERROR: "false"

  before_script:

    - python -m pip install --upgrade pip

    - |
      if [ -f whitehax_scans/requirements.txt ]; then
        pip install -r whitehax_scans/requirements.txt
      else
        pip install requests
      fi

  script:

    - echo "Initializing WhiteHax DAST against runtime environment"
    - echo "Target URL is $TARGET_URL"
    - python whitehax_scans/whitehax_tests.py

  artifacts:

    name: "whitehax-dast-findings"

    when: always

    paths:
      - gl-dast-report.json

    reports:
      dast: gl-dast-report.json`}
        </pre>

      </section>


      {/* =====================================================
          PYTHON DAST BRIDGE
      ===================================================== */}
      <section className="guide-section">

        <h2>
          {t(
            "Helpgl.recommendedPythonDastBridge.title",
            "5. Passerelle de rapport DAST Python recommandée"
          )}
        </h2>

        <p>
          {t(
            "Helpgl.recommendedPythonDastBridge.p1_prefix",
            "Enregistrez le fichier suivant sous"
          )}
          <code> whitehax_scans/whitehax_tests.py </code>
          {t(
            "Helpgl.recommendedPythonDastBridge.p1_suffix",
            "."
          )}
        </p>


        <div className="warning-box">

          <strong>
            {t(
              "Helpgl.recommendedPythonDastBridge.warningBox.title",
              "Important"
            )}
          </strong>

          <p>
            {t(
              "Helpgl.recommendedPythonDastBridge.warningBox.p1",
              "La section d'appel à l'API REST est intentionnellement laissée sous forme de TODO car le point de terminaison WhiteHaX exact, la charge utile de requête, le comportement de sondage et les champs de réponse sont spécifiques à la plateforme."
            )}
          </p>

        </div>


        <pre className="code-block">
{`#!/usr/bin/env python3

\"\"\"
Passerelle de rapport DAST GitLab pour WhiteHax.

Cet exemple conserve l'adaptateur d'API REST WhiteHax sous forme d'espace réservé
et se concentre sur un comportement CI GitLab stable, l'hygiène de la structure
des rapports, la normalisation des sévérités et la génération d'artefacts sécurisée
en cas d'erreur.
\"\"\"

import hashlib
import json
import os
import sys
import traceback

from datetime import datetime, timezone
from pathlib import Path
from urllib.parse import urljoin, urlparse

import requests


OUTPUT_FILE = Path("gl-dast-report.json")


GITLAB_SEVERITIES = {
    "critical": "Critical",
    "high": "High",
    "medium": "Medium",
    "warning": "Medium",
    "low": "Low",
    "info": "Info",
    "informational": "Info",
    "unknown": "Unknown",
}


def now_utc():
    return datetime.now(
        timezone.utc
    ).isoformat().replace("+00:00", "Z")


def require_env(name):

    value = os.environ.get(name)

    if not value:
        raise RuntimeError(
            f"Variable d'environnement requise manquante : {name}"
        )

    return value


def normalize_severity(value):

    return GITLAB_SEVERITIES.get(
        str(value or "").strip().lower(),
        "Unknown"
    )


def stable_finding_id(
    finding,
    target_url
):

    raw = "|".join(
        [
            str(finding.get("id", "")),
            str(finding.get("uri", "")),
            str(finding.get("description", "")),
            target_url,
        ]
    )

    return (
        "whitehax-"
        + hashlib.sha256(
            raw.encode("utf-8")
        ).hexdigest()[:32]
    )


def normalize_finding(raw):

    return {
        "id": str(
            raw.get("id")
            or raw.get("rule_id")
            or "WHX-GENERIC"
        ),

        "title": str(
            raw.get("title")
            or raw.get("name")
            or "Détection WhiteHax"
        ),

        "description": str(
            raw.get("description")
            or raw.get("message")
            or ""
        ),

        "severity": normalize_severity(
            raw.get("severity")
        ),

        "uri": str(
            raw.get("uri")
            or raw.get("path")
            or ""
        ),
    }


def run_whitehax_scan(
    whitehax_url,
    api_key,
    target_url
):

    \"\"\"
    TODO : Remplacez cet espace réservé par le flux de travail
    réel de l'API REST WhiteHax.

    Comportement attendu :

    - s'authentifier avec WHITEHAX_API_KEY sans l'afficher
    - soumettre ou déclencher une analyse pour TARGET_URL
    - sonder jusqu'à ce que l'analyse soit terminée ou échoue
    - récupérer les détections de l'analyse terminée
    - renvoyer une liste de dictionnaires que normalize_finding() peut consommer.
    \"\"\"

    raise NotImplementedError(
        "L'adaptateur d'API REST WhiteHax est spécifique au projet."
    )


def build_report(
    findings,
    target_url,
    execution_error=None
):

    start_time = now_utc()

    vulnerabilities = []

    parsed_target = urlparse(
        target_url
    )


    for raw in findings:

        finding = normalize_finding(
            raw
        )

        path = (
            "/"
            + finding["uri"].lstrip("/")
            if finding["uri"]
            else "/"
        )

        full_url = urljoin(
            target_url.rstrip("/") + "/",
            finding["uri"].lstrip("/")
        )


        vulnerabilities.append(
            {
                "id": stable_finding_id(
                    finding,
                    target_url
                ),

                "category": "dast",

                "name": finding["title"],

                "message":
                    finding["description"]
                    or finding["title"],

                "description":
                    finding["description"]
                    or finding["title"],

                "severity":
                    finding["severity"],

                "confidence": "Confirmed",

                "scanner": {
                    "id": "whitehax_engine",
                    "name": "WhiteHax Scan Core",
                },

                "identifiers": [
                    {
                        "type": "whitehax_id",
                        "name": finding["id"],
                        "value": finding["id"],
                    }
                ],

                "links": [
                    {
                        "url": full_url
                    }
                ],

                "location": {
                    "hostname":
                        parsed_target.hostname
                        or target_url,

                    "path": path,
                },
            }
        )


    status = "success"


    if execution_error:

        status = "failure"


        vulnerabilities.append(
            {
                "id": stable_finding_id(
                    {
                        "id": "WHX-SCAN-ERROR",
                        "uri": "/",
                        "description":
                            execution_error,
                    },
                    target_url,
                ),

                "category": "dast",

                "name":
                    "Erreur d'exécution de l'analyse WhiteHax",

                "message":
                    execution_error[:4000],

                "description":
                    execution_error[:4000],

                "severity": "Unknown",

                "confidence": "Confirmed",

                "scanner": {
                    "id": "whitehax_engine",
                    "name": "WhiteHax Scan Core",
                },

                "identifiers": [
                    {
                        "type": "whitehax_error",
                        "name": "WHX-SCAN-ERROR",
                        "value": "WHX-SCAN-ERROR",
                    }
                ],

                "location": {
                    "hostname":
                        parsed_target.hostname
                        or target_url,

                    "path": "/",
                },
            }
        )


    return {

        "version": "15.0.0",

        "scan": {

            "analyzer": {
                "id": "whitehax-dast",
                "name": "WhiteHax DAST Plugin",

                "vendor": {
                    "name": "WhiteHax Sec"
                },

                "version": "1.0.0",
            },


            "scanner": {
                "id": "whitehax_engine",
                "name": "WhiteHax Scan Core",

                "vendor": {
                    "name": "WhiteHax Sec"
                },

                "version": "1.0.0",
            },


            "type": "dast",

            "start_time": start_time,

            "end_time": now_utc(),

            "status": status,
        },


        "vulnerabilities":
            vulnerabilities,
    }


def write_report(payload):

    OUTPUT_FILE.write_text(
        json.dumps(
            payload,
            indent=2
        ),
        encoding="utf-8"
    )

    print(
        f"[INFO] Rapport DAST GitLab écrit dans {OUTPUT_FILE}"
    )


def main():

    target_url = (
        os.environ.get("TARGET_URL")
        or os.environ.get("CI_ENVIRONMENT_URL")
        or ""
    )


    try:

        whitehax_url = require_env(
            "WHITEHAX_URL"
        )

        api_key = require_env(
            "WHITEHAX_API_KEY"
        )

        target_url = (
            target_url
            or require_env("TARGET_URL")
        )


        findings = run_whitehax_scan(
            whitehax_url,
            api_key,
            target_url
        )


        write_report(
            build_report(
                findings,
                target_url
            )
        )


    except Exception as exc:

        print(
            "[ERROR] L'analyse WhiteHax a échoué :",
            exc,
            file=sys.stderr
        )

        traceback.print_exc()


        write_report(
            build_report(
                [],
                target_url
                or "https://unknown-target.invalid",
                str(exc)
            )
        )


        if os.environ.get(
            "WHITEHAX_FAIL_ON_ERROR",
            "false"
        ).lower() == "true":

            return 1


    return 0


if __name__ == "__main__":
    raise SystemExit(main())`}
        </pre>

      </section>


      {/* =====================================================
          VALIDATION NOTES
      ===================================================== */}
      <section className="guide-section">

        <h2>{t("Helpgl.validationNotes.title", "6. Notes de validation")}</h2>

        <div className="table-wrapper">

          <table>

            <thead>
              <tr>
                <th>
                  {t("Helpgl.validationNotes.table.headers.area", "Domaine")}
                </th>
                <th>
                  {t("Helpgl.validationNotes.table.headers.status", "Statut")}
                </th>
                <th>
                  {t(
                    "Helpgl.validationNotes.table.headers.recommendedHandling",
                    "Gestion recommandée"
                  )}
                </th>
              </tr>
            </thead>


            <tbody>

              {(
                t("Helpgl.validationNotes.table.rows", {
                  returnObjects: true,
                  defaultValue: [
                    {
                      area: "Déclencheur de pipeline",
                      status: "Nécessite une correction",
                      handling:
                        "GitLab ne reflète pas le deployment_status de GitHub. Utilisez une étape ultérieure après le déploiement, un transfert dotenv ou un pipeline déclenché avec WHITEHAX_TARGET_URL.",
                    },
                    {
                      area: "Condition de règle",
                      status: "Correctif de bogue",
                      handling:
                        "Utilisez CI_PIPELINE_SOURCE et non CI_PIPIELINE_SOURCE. Mettez les expressions de règles entre guillemets dans .gitlab-ci.yml.",
                    },
                    {
                      area: "Artefact DAST",
                      status: "Valide",
                      handling:
                        "artifacts:reports:dast : gl-dast-report.json est le raccord de rapport GitLab correct.",
                    },
                    {
                      area: "Visibilité du rapport",
                      status: "Niveau Ultimate",
                      handling:
                        "Les rapports DAST et l'intégration des rapports de vulnérabilité nécessitent GitLab Ultimate pour les tableaux de bord et widgets de sécurité natifs.",
                    },
                    {
                      area: "Adaptateur d'API REST",
                      status: "Spécifique au projet",
                      handling:
                        "Laissez le flux d'envoi/sondage/récupération de l'API WhiteHaX comme espace réservé jusqu'à ce que le contrat du point de terminaison soit confirmé.",
                    },
                    {
                      area: "Génération de rapports",
                      status: "Renforcé",
                      handling:
                        "Utilisez des identifiants de vulnérabilité stables, une sévérité normalisée, un emplacement limité au nom d'hôte, des horodatages UTC et une génération d'artefacts sécurisée contre les erreurs.",
                    },
                  ],
                }) || []
              ).map((row, idx) => {
                let statusClass = "status";
                if (row.status === "Nécessite une correction") statusClass += " needs";
                else if (row.status === "Correctif de bogue") statusClass += " fixed";
                else if (row.status === "Valide") statusClass += " valid";
                else if (row.status === "Niveau Ultimate") statusClass += " ultimate";
                else if (row.status === "Spécifique au projet") statusClass += " project";
                else if (row.status === "Renforcé") statusClass += " hardened";

                return (
                  <tr key={idx}>
                    <td>{row.area}</td>
                    <td>
                      <span className={statusClass}>{row.status}</span>
                    </td>
                    <td>{row.handling}</td>
                  </tr>
                );
              })}

            </tbody>

          </table>

        </div>

      </section>


      {/* =====================================================
          IMPLEMENTATION CHECKLIST
      ===================================================== */}
      <section className="guide-section">

        <h2>
          {t("Helpgl.implementationChecklist.title", "7. Liste de contrôle de mise en œuvre")}
        </h2>

        <div className="checklist">

          <label>
            <input type="checkbox" />
            {t(
              "Helpgl.implementationChecklist.item1",
              "Créez les variables CI/CD WHITEHAX_URL et WHITEHAX_API_KEY et configurez-les comme masquées/protégées si nécessaire."
            )}
          </label>


          <label>
            <input type="checkbox" />
            {t(
              "Helpgl.implementationChecklist.item2",
              "Transmettez l'URL de l'application déployée en tant que WHITEHAX_TARGET_URL, TARGET_URL ou CI_ENVIRONMENT_URL."
            )}
          </label>


          <label>
            <input type="checkbox" />
            {t(
              "Helpgl.implementationChecklist.item3",
              "Mappez les détections réelles de WhiteHaX dans id, title, description, severity et uri."
            )}
          </label>


          <label>
            <input type="checkbox" />
            {t(
              "Helpgl.implementationChecklist.item4",
              "Validez gl-dast-report.json par rapport au schéma de rapport de sécurité de GitLab avant le déploiement."
            )}
          </label>


          <label>
            <input type="checkbox" />
            {t(
              "Helpgl.implementationChecklist.item5",
              "Décidez si WHITEHAX_FAIL_ON_ERROR doit rester à false pour le mode rapport uniquement ou passer à true pour le mode bloquant."
            )}
          </label>

        </div>

      </section>


      {/* =====================================================
          COMMON PITFALLS
      ===================================================== */}
      <section className="guide-section">

        <h2>{t("Helpgl.commonPitfalls.title", "8. Pièges courants")}</h2>

        <ul className="bullet-list">

          <li>
            {t(
              "Helpgl.commonPitfalls.item1",
              "N'utilisez pas CI_PIPIELINE_SOURCE ; la variable prédéfinie correcte est CI_PIPELINE_SOURCE."
            )}
          </li>


          <li>
            {t(
              "Helpgl.commonPitfalls.item2",
              "Ne supposez pas qu'un travail post-déploiement hérite automatiquement de CI_ENVIRONMENT_URL sans le transmettre ou le définir explicitement."
            )}
          </li>


          <li>
            {t(
              "Helpgl.commonPitfalls.item3",
              "N'affichez pas les clés d'API WhiteHaX ni les en-têtes d'autorisation dans les journaux des travaux GitLab."
            )}
          </li>


          <li>
            {t(
              "Helpgl.commonPitfalls.item4",
              "N'utilisez pas d'identifiants instables tels que des index de boucles pour le suivi à long terme des vulnérabilités."
            )}
          </li>


          <li>
            {t(
              "Helpgl.commonPitfalls.item5",
              "N'émettez pas de valeurs de sévérité brutes du scanner à moins qu'elles ne correspondent au vocabulaire de sévérité autorisé par GitLab."
            )}
          </li>


          <li>
            {t(
              "Helpgl.commonPitfalls.item6",
              "N'omettez pas artifacts:paths : si les utilisateurs ont besoin de consulter le fichier JSON d'artefact, incluez artifacts:paths en plus de artifacts:reports:dast."
            )}
          </li>

        </ul>


        <div className="final-note">

          <strong>
            {t(
              "Helpgl.commonPitfalls.finalNote.title",
              "Recommandation pour la production"
            )}
          </strong>

          <p>
            {t(
              "Helpgl.commonPitfalls.finalNote.p1",
              "Validez le rapport DAST GitLab généré par rapport au schéma de rapport de sécurité de GitLab avant le déploiement et confirmez que l'URL d'exécution est explicitement transmise au travail de vérification de sécurité."
            )}
          </p>

        </div>

      </section>

    </div>
  );
}


/* =========================================================
   INTERNAL CSS
   ========================================================= */

const style = document.createElement("style");

style.innerHTML = `

  * {
    box-sizing: border-box;
  }


  .whx-gitlab-guide {

    min-height: 100vh;

    padding: 40px 50px 80px;

    background: #0F172A;

    color: #CBD5E1;

    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      Arial,
      sans-serif;

    line-height: 1.65;
  }


  /* =========================
     HEADER
  ========================= */

  .guide-header {

    max-width: 1100px;

    margin: 0 auto 35px;

    padding-bottom: 25px;

    border-bottom:
      1px solid #334155;
  }


  .guide-header h1 {

    margin: 0 0 10px;

    color: #F8FAFC;

    font-size: 30px;

    font-weight: 700;

    line-height: 1.3;
  }


  .guide-header p {

    margin: 0;

    color: #94A3B8;

    font-size: 15px;
  }


  /* =========================
     SECTIONS
  ========================= */

  .guide-section {

    max-width: 1100px;

    margin: 0 auto 30px;

    padding: 28px;

    background: #111827;

    border:
      1px solid #263449;

    border-radius: 12px;

    box-shadow:
      0 8px 25px rgba(0, 0, 0, 0.18);
  }


  .guide-section h2 {

    margin: 0 0 18px;

    color: #F8FAFC;

    font-size: 22px;

    font-weight: 700;
  }


  .guide-section h3 {

    margin: 26px 0 12px;

    color: #E2E8F0;

    font-size: 17px;
  }


  .guide-section p {

    color: #CBD5E1;

    font-size: 14px;
  }


  .guide-section ol,
  .guide-section ul {

    color: #CBD5E1;

    font-size: 14px;
  }


  .guide-section li {

    margin-bottom: 10px;
  }


  /* =========================
     INLINE CODE
  ========================= */

  .guide-section code {

    display: inline-block;

    padding: 1px 6px;

    margin: 0 2px;

    border-radius: 4px;

    background: #020617;

    color: #93C5FD;

    font-family:
      Consolas,
      "Courier New",
      monospace;

    font-size: 12px;
  }


  /* =========================
     INFO BOX
  ========================= */

  .info-box {

    margin: 22px 0;

    padding: 18px 20px;

    background: #172554;

    border-left:
      4px solid #3B82F6;

    border-radius: 7px;
  }


  .info-box strong {

    color: #93C5FD;

    font-size: 14px;
  }


  .info-box p {

    margin:
      8px 0 0;

    color: #CBD5E1;
  }


  /* =========================
     WARNING BOX
  ========================= */

  .warning-box {

    margin: 20px 0;

    padding: 18px 20px;

    background: #3F2A05;

    border-left:
      4px solid #F59E0B;

    border-radius: 7px;
  }


  .warning-box strong {

    color: #FBBF24;

    font-size: 14px;
  }


  .warning-box p {

    margin-bottom: 0;
  }


  /* =========================
     CODE BLOCK
  ========================= */

  .code-block {

    width: 100%;

    overflow-x: auto;

    margin: 20px 0;

    padding: 20px;

    background: #020617;

    color: #CBD5E1;

    border:
      1px solid #1E293B;

    border-radius: 8px;

    font-family:
      Consolas,
      "Courier New",
      monospace;

    font-size: 13px;

    line-height: 1.6;

    white-space: pre;

    tab-size: 2;

    box-shadow:
      inset 0 1px 0
      rgba(255,255,255,0.02);
  }


  /* =========================
     BULLET LIST
  ========================= */

  .bullet-list {

    padding-left: 25px;

    margin-bottom: 0;
  }


  /* =========================
     TABLE
  ========================= */

  .table-wrapper {

    width: 100%;

    overflow-x: auto;
  }


  .table-wrapper table {

    width: 100%;

    min-width: 800px;

    border-collapse: collapse;

    font-size: 13px;
  }


  .table-wrapper th {

    padding: 14px;

    text-align: left;

    background: #1E293B;

    color: #F8FAFC;

    border:
      1px solid #334155;

    font-weight: 600;
  }


  .table-wrapper td {

    padding: 14px;

    vertical-align: top;

    color: #CBD5E1;

    border:
      1px solid #334155;

    line-height: 1.55;
  }


  .table-wrapper tr:nth-child(even) td {

    background: #0F172A;
  }


  .table-wrapper tr:hover td {

    background: #172033;
  }


  /* =========================
     STATUS BADGES
  ========================= */

  .status {

    display: inline-block;

    padding:
      4px 9px;

    border-radius: 5px;

    font-size: 11px;

    font-weight: 600;

    white-space: nowrap;
  }


  .status.needs {

    background: #451A03;

    color: #FDBA74;
  }


  .status.fixed {

    background: #064E3B;

    color: #6EE7B7;
  }


  .status.valid {

    background: #064E3B;

    color: #6EE7B7;
  }


  .status.ultimate {

    background: #312E81;

    color: #C4B5FD;
  }


  .status.project {

    background: #422006;

    color: #FCD34D;
  }


  .status.hardened {

    background: #172554;

    color: #93C5FD;
  }


  /* =========================
     CHECKLIST
  ========================= */

  .checklist {

    display: flex;

    flex-direction: column;

    gap: 12px;
  }


  .checklist label {

    display: flex;

    align-items: flex-start;

    gap: 10px;

    padding:
      13px 15px;

    background: #0F172A;

    border:
      1px solid #263449;

    border-radius: 7px;

    color: #CBD5E1;

    font-size: 14px;

    cursor: pointer;

    transition:
      border-color 0.2s ease,
      background 0.2s ease;
  }


  .checklist label:hover {

    background: #172033;

    border-color: #3B82F6;
  }


  .checklist input {

    width: 16px;

    height: 16px;

    margin-top: 4px;

    accent-color: #3B82F6;

    cursor: pointer;
  }


  /* =========================
     FINAL NOTE
  ========================= */

  .final-note {

    margin-top: 25px;

    padding:
      18px 20px;

    background: #172554;

    border:
      1px solid #1D4ED8;

    border-radius: 8px;
  }


  .final-note strong {

    color: #93C5FD;

    font-size: 14px;
  }


  .final-note p {

    margin:
      7px 0 0;

    color: #CBD5E1;
  }


  /* =========================
     RESPONSIVE
  ========================= */

  @media (max-width: 900px) {

    .whx-gitlab-guide {

      padding:
        30px 20px 60px;
    }


    .guide-section {

      padding: 22px;
    }


    .guide-header h1 {

      font-size: 25px;
    }
  }


  @media (max-width: 600px) {

    .whx-gitlab-guide {

      padding:
        20px 12px 50px;
    }


    .guide-section {

      padding: 18px;

      border-radius: 9px;
    }


    .guide-header h1 {

      font-size: 22px;
    }


    .guide-header p {

      font-size: 13px;
    }


    .guide-section h2 {

      font-size: 19px;
    }


    .guide-section p,
    .guide-section li {

      font-size: 13px;
    }


    .code-block {

      padding: 14px;

      font-size: 11px;

      line-height: 1.5;
    }


    .table-wrapper table {

      min-width: 700px;
    }
  }

`;

document.head.appendChild(style);